/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useMemo} from 'react';
import {MarkdownContext, parse} from '~/lib/markdown/renderers';
import type {MessageRecord} from '~/records/MessageRecord';
import {DebugModal, type DebugTab, SummaryItem} from './DebugModal';

interface MessageDebugModalProps {
	title: string;
	message: MessageRecord;
}

export const MessageDebugModal: React.FC<MessageDebugModalProps> = observer(({title, message}) => {
	const {t} = useLingui();
	const recordJsonData = useMemo(() => message.toJSON(), [message]);

	const astData = useMemo(() => {
		if (!message.content) return null;

		const startTime = performance.now();
		const nodes = parse({
			content: message.content,
			context: MarkdownContext.STANDARD_WITH_JUMBO,
		});
		const endTime = performance.now();

		return {
			nodes,
			parseTime: endTime - startTime,
		};
	}, [message.content]);

	const tabs: Array<DebugTab> = [
		{
			id: 'record',
			label: t`Message Record`,
			data: recordJsonData,
		},
		{
			id: 'ast',
			label: t`Message AST`,
			data: astData?.nodes ?? null,
			summary: astData ? (
				<SummaryItem label={t`Total Parsing Time:`} value={`${astData.parseTime.toFixed(2)} ms`} />
			) : null,
		},
	];

	return <DebugModal title={title} tabs={tabs} />;
});
